import {useSerialAsyncEffectExceptFirstRender} from '@wandb/weave/common/util/hooks';
import {unixTimestampMSFromUTCString} from '@wandb/weave/common/util/time';
import {isNotNullOrUndefined, Nullable} from '@wandb/weave/common/util/types';
import ApolloClient, {ApolloQueryResult} from 'apollo-client';
import produce from 'immer';
import _ from 'lodash';
import React, {useCallback, useEffect, useState} from 'react';
import {useApolloClient} from 'react-apollo';
import ReactTimeago from 'react-timeago';

import ConflictWarningModal from '../../components/ConflictWarningModal';
import {WBSlateElementType} from '../../components/Slate/WBSlate/types';
import {isEmptySlateValue} from '../../components/Slate/WBSlate/utils';
import {
  EntityProfileQuery,
  FeaturedReportsMetadataDocument,
  FeaturedReportsMetadataQuery,
  FeaturedReportsMetadataQueryVariables,
  ProfilePageQuery,
  useUpsertView2Mutation,
} from '../../generated/graphql';
import {captureError} from '../../integrations';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {
  entityReportCreated,
  profileShareViewed,
} from '../../services/analytics';
import {usePrevious} from '../../state/hooks';
import {useViewer} from '../../state/viewer/hooks';
import {Viewer} from '../../state/viewer/types';
import {fromNow} from '../../util/date';
import {GallerySpec} from '../../util/gallery';
import {imgSrcWithDefault} from '../../util/images';
import {getStringValueFromQueryString} from '../../util/query-string';
import {project, reportView} from '../../util/urls';
import {
  ENTITY_PAGE_INITIAL_STATE,
  EntityPageState,
  EntityPageStateV0,
  EntityPageStateV1,
  EntityPageStateV2,
  ShowcasedProject,
  ShowcasedReport,
} from './context';
import {isShareProfileType, SHARE_PROFILE_QS_KEY} from './ShareProfileTrigger';

export const SECTION_DROPZONE_PADDING_PX = 20;

type CreateEntityProfileParams = {
  profilePageData: ProfilePageQuery | undefined;
  entityIsTeam: boolean;
  gallerySpec: GallerySpec;
  entityName: string;
};

export function useCreateEntityProfile({
  profilePageData,
  entityIsTeam,
  gallerySpec,
  entityName,
}: CreateEntityProfileParams): () => Promise<void> {
  const apolloClient = useApolloClient();
  const [upsertView] = useUpsertView2Mutation();
  const viewer = useViewer();

  return useCallback(async () => {
    const showcasedReportsFromOld: ShowcasedReport[] =
      getShowcasedReportsFromOld(profilePageData);

    const showcasedReportsFromSubmittedFCPosts: ShowcasedReport[] =
      !entityIsTeam
        ? await getShowcasedReportsFromSubmittedFCPosts({
            gallerySpec,
            viewer,
            apolloClient,
          })
        : [];

    await upsertView({
      variables: {
        entityName,
        type: `entity-profile`,
        name: `entity-profile-${entityName}`,
        spec: JSON.stringify({
          ...ENTITY_PAGE_INITIAL_STATE,
          showcasedReports: _.uniqBy(
            [
              ...showcasedReportsFromOld,
              ...showcasedReportsFromSubmittedFCPosts,
            ],
            `id`
          ),
        }),
      },
    });
  }, [
    apolloClient,
    upsertView,
    viewer,
    profilePageData,
    entityIsTeam,
    gallerySpec,
    entityName,
  ]);
}

function getShowcasedReportsFromOld(
  profilePageData: ProfilePageQuery | undefined
): ShowcasedReport[] {
  const oldShowcasedReports =
    profilePageData?.user?.views?.edges
      .map(e => e.node)
      .filter(isNotNullOrUndefined)
      .filter(r => r.showcasedAt != null) ?? [];

  const oldShowcasedReportsRecentLast = _.sortBy(oldShowcasedReports, r =>
    r.showcasedAt != null ? new Date(r.showcasedAt) : new Date()
  );

  return oldShowcasedReportsRecentLast
    .map(getShowcasedReport)
    .filter(isNotNullOrUndefined);
}

type GetShowcasedReportsFromSubmittedFCPostsParams = {
  gallerySpec: GallerySpec;
  viewer: Viewer | undefined;
  apolloClient: ApolloClient<object>;
};

async function getShowcasedReportsFromSubmittedFCPosts({
  gallerySpec,
  viewer,
  apolloClient,
}: GetShowcasedReportsFromSubmittedFCPostsParams): Promise<ShowcasedReport[]> {
  const allFCReports = [
    ...gallerySpec.reportIDsWithTagV2IDs,
    ...gallerySpec.reportIDsWithTagIDs,
  ];

  const reportsSubmittedByViewer = allFCReports.filter(r =>
    r.authors.some(a => a.username === viewer?.username)
  );

  const reportsSubmittedByViewerRecentFirst = _.sortBy(
    reportsSubmittedByViewer,
    r => -new Date(r.addedAt).valueOf()
  );

  const submittedFCPostIDs = _.uniq(
    reportsSubmittedByViewerRecentFirst.map(r => r.id)
  );

  const {data: submittedFCPostMetadataData} = await apolloClient.query<
    FeaturedReportsMetadataQuery,
    FeaturedReportsMetadataQueryVariables
  >({
    query: FeaturedReportsMetadataDocument,
    variables: {ids: submittedFCPostIDs},
  });

  const submittedFCPostMetadatas =
    submittedFCPostMetadataData.views?.edges
      .map(e => e.node)
      .filter(isNotNullOrUndefined) ?? [];

  return submittedFCPostMetadatas
    .map(getShowcasedReport)
    .filter(isNotNullOrUndefined);
}

type ReportData = ReportDataWithProject | ReportDataWithEntityName;

type ReportDataBase = {
  id: string;
  displayName?: Nullable<string>;
  description?: Nullable<string>;
  previewUrl?: Nullable<string>;
  createdAt: string;
  updatedAt?: Nullable<string>;
  viewCount: number;
};

type ReportDataWithProject = ReportDataBase & {
  project?: Nullable<{
    name: string;
    entityName: string;
  }>;
};

type ReportDataWithEntityName = ReportDataBase & {entityName: string};

export function getShowcasedReport(r: ReportData): ShowcasedReport | null {
  const entityName =
    `entityName` in r && r.entityName
      ? r.entityName
      : `project` in r
      ? r.project?.entityName
      : undefined;
  const projectName = `project` in r ? r.project?.name : undefined;
  if (!entityName || !r.displayName) {
    return null;
  }
  return {
    id: r.id,
    url: reportView({
      entityName,
      projectName,
      reportID: r.id,
      reportName: r.displayName,
    }),
    name: r.displayName,
    description: r.description ?? ``,
    imageURL: imgSrcWithDefault(r.previewUrl),
    lastEdit: fromNow(new Date(r.updatedAt ?? r.createdAt)),
    viewCount: r.viewCount,
  };
}

export type ProjectData = {
  id: string;
  name: string;
  entityName: string;
  user?: Nullable<{username?: Nullable<string>}>;
  runCount: number;
  lastActive?: Nullable<string>;
  access: string;
};

export function getShowcasedProject(p: ProjectData): ShowcasedProject | null {
  const username = p.user?.username;
  const lastActive = p.lastActive;
  if (!username || !lastActive) {
    return null;
  }
  return {
    id: p.id,
    url: project({name: p.name, entityName: p.entityName}),
    name: p.name,
    user: username,
    entityName: p.entityName,
    lastRun: fromNow(new Date(lastActive)),
    runCount: p.runCount,
  };
}

export function parseProfileSpec(spec: string): EntityPageState {
  const state = JSON.parse(spec);

  if (state.reportSpec != null && isEmptySlateValue(state.reportSpec)) {
    state.reportSpec = null;
  }

  const stateV1 = migrateProfileStateV0ToV1(state);
  const stateV2 = migrateProfileStateV1ToV2(stateV1);

  return stateV2;
}

// TODO(axel): the amount of `any`s here is criminal. Find a way to do this better ASAP.
function migrateProfileStateV0ToV1(
  state: EntityPageStateV0
): EntityPageStateV1 {
  if (state.version >= 1) {
    return state as unknown as EntityPageStateV1;
  }
  if ((state.version as number) !== 0) {
    throw new Error(`Must migrate profile state to v0 before migrating to v1`);
  }
  return produce(state, draft => {
    (draft as any).version = 1;
    if ((draft as any).showcasedReportIDs == null) {
      (draft as any).showcasedReportIDs = draft.showcasedReports.map(r => r.id);
    }
  }) as unknown as EntityPageStateV1;
}

// TODO(axel): the amount of `any`s here is criminal. Find a way to do this better ASAP.
function migrateProfileStateV1ToV2(
  state: EntityPageStateV1
): EntityPageStateV2 {
  if (state.version >= 2) {
    return state as unknown as EntityPageStateV2;
  }
  if ((state.version as number) !== 1) {
    throw new Error(`Must migrate profile state to v1 before migrating to v2`);
  }
  return produce(state, draft => {
    (draft as any).version = 2;
    if ((draft as any).showcasedProjectIDs == null) {
      (draft as any).showcasedProjectIDs = [];
    }
  }) as unknown as EntityPageStateV2;
}

type ConflictData = {
  updatedAtMS: number;
  updatedByUsername: string;
};

type ConflictDataWithOverwrite = ConflictData & {
  overwrite: () => Promise<void>;
};

type UseAutosaveProfileViewParams = {
  entityName: string;
  profileViewID: string | null;
  entityProfileUpdatedAtMS: number | null;
  readOnly: boolean;
  entityPageState: EntityPageState;
  refetchEntityProfile: () => Promise<ApolloQueryResult<EntityProfileQuery>>;
};

// Save view whenever the `EntityPageState` changes.
// Serial updates should not overlap (see `useSerialAsyncEffects`).
// Another user editing simultaneously will trigger a conflict warning.
export function useAutosaveProfileView({
  entityName,
  profileViewID,
  entityProfileUpdatedAtMS,
  readOnly,
  entityPageState,
  refetchEntityProfile,
}: UseAutosaveProfileViewParams): React.ReactNode {
  const viewer = useViewer();
  const [upsertView] = useUpsertView2Mutation();

  const [conflictDataWithOverwrite, setConflictDataWithOverwrite] =
    useState<ConflictDataWithOverwrite | null>(null);

  const prevEntityPageState = usePrevious(entityPageState);

  useSerialAsyncEffectExceptFirstRender(async () => {
    if (readOnly || profileViewID == null) {
      return;
    }

    // THIS SHOULD NEVER HAPPEN. But it did happen ONCE while testing...
    // Somehow a `reportSpec` of `[]` got saved, even though that's only possible
    // when there is no actual profile view created yet and the page is
    // loaded by another user with `readOnly = true`.
    // This is obviously a really bad case, so let's intercept it if it happens in the wild.
    // If it does, we will investigate further.
    if (
      entityPageState.reportSpec != null &&
      entityPageState.reportSpec.length === 0
    ) {
      captureError(
        `attempted to save empty reportSpec`,
        `EntityPageContextProvider`
      );
      return;
    }

    const conflictData = await getPotentialConflict({
      viewer,
      entityProfileUpdatedAtMS,
      refetchEntityProfile,
    });

    // If there is no conflict, just save and move on
    if (conflictData == null) {
      await save();
      return;
    }

    // If there is a conflict, wait until the user explicitly overwrites
    await new Promise<void>(resolve => {
      setConflictDataWithOverwrite({
        ...conflictData,
        overwrite: async () => {
          await save();
          setConflictDataWithOverwrite(null);
          resolve();
        },
      });
    });
  }, [entityPageState]);

  if (conflictDataWithOverwrite == null) {
    return null;
  }

  const {updatedAtMS, updatedByUsername, overwrite} = conflictDataWithOverwrite;

  return (
    <ConflictWarningModal
      onOverwrite={overwrite}
      onCancel={() => window.location.reload()}>
      <p style={{marginBottom: 24}}>
        This profile page was modified{' '}
        <ReactTimeago date={updatedAtMS} live={false} /> by {updatedByUsername}.
        Do you want to overwrite their changes?
      </p>
    </ConflictWarningModal>
  );

  // Helper functions

  async function save(): Promise<void> {
    if (
      prevEntityPageState?.reportSpec == null &&
      entityPageState.reportSpec != null
    ) {
      entityReportCreated({entityName});
    }

    await upsertView({
      variables: {
        id: profileViewID,
        spec: JSON.stringify(entityPageState),
      },
    });
    await refetchEntityProfile();
  }
}

type GetPotentialConflictParams = {
  viewer: Viewer | undefined;
  entityProfileUpdatedAtMS: number | null;
  refetchEntityProfile: () => Promise<ApolloQueryResult<EntityProfileQuery>>;
};

async function getPotentialConflict({
  viewer,
  entityProfileUpdatedAtMS,
  refetchEntityProfile,
}: GetPotentialConflictParams): Promise<ConflictData | null> {
  // Get the freshest state of the entity profile view
  const updatedProfileView = await refetchEntityProfile();
  const updatedAtStr = updatedProfileView.data.entity?.profileView?.updatedAt;
  const updatedByUsername =
    updatedProfileView.data.entity?.profileView?.updatedBy?.username;

  if (updatedAtStr == null || updatedByUsername == null) {
    return null;
  }

  // If our current copy is fresh, we have no conflict here
  const updatedAtMS = unixTimestampMSFromUTCString(updatedAtStr);
  if (
    entityProfileUpdatedAtMS != null &&
    entityProfileUpdatedAtMS >= updatedAtMS
  ) {
    return null;
  }

  // If the last updater is the current user, we have no conflict here
  if (updatedByUsername === viewer?.username) {
    return null;
  }

  return {
    updatedAtMS,
    updatedByUsername,
  };
}

export function getEntityProfileInitialValue(
  entityName: string,
  isTeam: boolean
): WBSlateElementType[] {
  return isTeam
    ? [
        {
          type: 'heading',
          level: 1,
          children: [{text: 'Team README'}],
        },
        {
          type: 'paragraph',
          children: [{text: ''}],
        },
        {
          type: 'heading',
          level: 2,
          children: [{text: `Welcome to ${entityName}`}],
        },
        {
          type: 'paragraph',
          children: [
            {
              text: 'Briefly describe what your team works on and its mission.\n',
            },
          ],
        },
        {
          type: 'heading',
          level: 2,
          children: [{text: 'Getting started'}],
        },
        {
          type: 'paragraph',
          children: [
            {text: '1. First step for new team members\n'},
            {text: '2. How to set up your environment\n'},
            {text: '3. Where to find documentation\n'},
          ],
        },
        {
          type: 'heading',
          level: 2,
          children: [{text: 'Communication'}],
        },
        {
          type: 'paragraph',
          children: [
            {text: '1. Slack channels: #team-channel\n'},
            {text: '2. Meeting schedule: Weekly on Thursdays at 10am\n'},
          ],
        },
        {
          type: 'heading',
          level: 2,
          children: [{text: 'Key resources'}],
        },
        {
          type: 'paragraph',
          children: [
            {text: '1. Link to documentation: [Team Wiki](url)\n'},
            {text: '2. Important tools: [Dashboard](url)\n'},
          ],
        },
      ]
    : [
        {
          type: 'heading',
          children: [{text: `Hello, I'm @${entityName}`}],
          level: 1,
        },
        {type: 'paragraph', level: 1, children: [{text: '🖥️ My role is...'}]},
        {
          type: 'paragraph',
          level: 1,
          children: [{text: '📚 My interests include...'}],
        },
        {
          type: 'paragraph',
          level: 1,
          children: [{text: "🪴 I'm currently learning..."}],
        },
        {
          type: 'paragraph',
          level: 1,
          children: [{text: "🚀 Let's collaborate on..."}],
        },
        {
          type: 'paragraph',
          level: 1,
          children: [{text: '✉️ You can reach me here...'}],
        },
        {type: 'paragraph', children: [{text: ''}]},
      ];
}

export function useTrackProfileShareView(entityName: string): void {
  const shareType = getStringValueFromQueryString(SHARE_PROFILE_QS_KEY);

  useEffect(() => {
    if (shareType != null && isShareProfileType(shareType)) {
      profileShareViewed({entityName, shareType});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
